import type { CreatorEntity, MemberData } from "shared/schema";
import type { Query } from "shelving";
import { assertProp, getDataKeys, getMidnight } from "shelving";
import { Page, PathButton } from "shared/react";
import { MEMBERS_MAX, MEMBER_ADMIN_STATUS, MEMBER_APPROVED_STATUS } from "shared/schema";
import { EntityHeader } from "../entity/EntityHeader.js";
import { GroupAdminCondition } from "./GroupAdminCondition.js";
import { useGroupContext } from "./GroupContext.js";
import { MemberList } from "./MemberList.js";

const MEMBER_FILTERS = {
	approved: "Approved",
	admin: "Admin",
	invited: "Invited",
	pending: "Pending",
	muted: "Muted",
	blocked: "Blocked",
};
type MemberFilter = keyof typeof MEMBER_FILTERS;

const MEMBER_QUERIES: {
	[K in MemberFilter]: Query<MemberData>;
} = {
	approved: { status: getDataKeys(MEMBER_APPROVED_STATUS), $order: "!updated" },
	admin: { status: getDataKeys(MEMBER_ADMIN_STATUS), $order: "!updated" },
	invited: { status: "invited", $order: "!updated" },
	pending: { status: "pending", $order: "!updated" },
	muted: { "muted>": getMidnight().getTime(), "$order": "!muted" },
	blocked: { status: "blocked", $order: "!updated" },
};

export function MembersPage({ filter = "approved" }: { filter?: string | undefined }): React.ReactElement {
	assertProp(MEMBER_FILTERS, filter);
	const { id: gid } = useGroupContext();
	const creator: CreatorEntity = `group:${gid}`;
	return (
		<Page path={`./members/${filter}`} title={`${MEMBER_FILTERS[filter]} Members`}>
			<EntityHeader entity={creator}>
				<h1>{`${MEMBER_FILTERS[filter]} members`}</h1>
			</EntityHeader>
			<GroupAdminCondition gid={gid}>
				<nav className="size-small space-none width-full fill-panel">
					<div className="flex-row width-wide">
						{Object.entries(MEMBER_FILTERS).map(([key, title]) => (
							<PathButton key={key} path={`../${key}`}>
								{title}
							</PathButton>
						))}
					</div>
				</nav>
			</GroupAdminCondition>
			{filter === "approved" ? (
				<section>
					<PathButton path={`../../invite`}>Invite member</PathButton>
				</section>
			) : null}
			<MemberList gid={gid} query={{ gid, ...MEMBER_QUERIES[filter], $limit: MEMBERS_MAX }} />
		</Page>
	);
}
